import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Calendar, CalendarType } from '@vendasta/meetings';
import { SubscriptionList } from '@vendasta/rx-utils';
import { BehaviorSubject, NEVER, Observable, from, of, timer } from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import {
  ALTERNATE_TRANSLATIONS_TOKEN,
  RECOMMENDED_GROUP_CALENDAR_SLUGS_TOKEN,
  RECOMMENDED_PERSONAL_CALENDAR_SLUGS_TOKEN,
} from '../../data-providers/providers';
import {
  CalendarSlugConfirmationDialogComponent,
  CalendarSlugConfirmationDialogData,
} from './calendar-slug-confirmation-dialog/calendar-slug-confirmation-dialog.component';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { POSTHOG_ACTIONS, POSTHOG_CATEGORIES, POSTHOG_KEYS } from '../../constants';
import { UnsavedChangesGuard } from '../../shared/unsaved-changes-guard.service';

@Component({
  selector: 'meeting-scheduler-calendar-slug-card',
  templateUrl: './calendar-slug-card.component.html',
  styleUrls: ['./calendar-slug-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CalendarSlugCardComponent implements OnInit, OnDestroy {
  @Input() setInitial: boolean;
  @Input() buttonLabel = this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.BUTTONS.SAVE');
  @Input() disableButtonIfValueUnchanged = false;
  @Input() enableTracking: boolean;
  @Input() navigateAfterSave: any[]; // Angular router expects any[]
  @Output() saveButtonClicked: EventEmitter<void> = new EventEmitter();

  @Input() set calendarId(id: string) {
    this.calendarId$$.next(id);
  }

  calendarId$$ = new BehaviorSubject('');

  linkPrefix$: Observable<string>;

  originalSlug: string;
  slugFormControl: UntypedFormControl;
  isSaving$$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  calendar$: Observable<Calendar>;
  recommendedSlugs$: Observable<string[]>;
  loading$: Observable<boolean>;
  description$: Observable<string>;

  slugChanged$$ = new BehaviorSubject(false);

  private readonly subscriptions = SubscriptionList.new();

  constructor(
    @Inject(RECOMMENDED_PERSONAL_CALENDAR_SLUGS_TOKEN)
    private readonly recommendedPersonalCalendarSlugs$: Observable<string[]>,
    @Inject(RECOMMENDED_GROUP_CALENDAR_SLUGS_TOKEN)
    private readonly recommendedGroupCalendarSlugs$: Observable<{ [externalCalendarId: string]: string[] }>,
    @Inject(ALTERNATE_TRANSLATIONS_TOKEN)
    private readonly alternateTranslations: Observable<{ [translationKey: string]: string }>,
    private readonly router: Router,
    private readonly fb: UntypedFormBuilder,
    private readonly cd: ChangeDetectorRef,
    private readonly alertService: SnackbarService,
    private readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly meetingSchedulerService: MeetingSchedulerStoreService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly unsavedChangesGuard: UnsavedChangesGuard,
  ) {
    this.slugFormControl = this.fb.control(
      this.originalSlug,
      [Validators.required, this.alphaNumericAndLengthSlugValidator()],
      this.slugNotTakenValidator(),
    );
    // Register forms to create a warning when leaving the page with changes in these forms
    this.unsavedChangesGuard.registerForms([this.slugFormControl]);
  }

  ngOnInit(): void {
    this.subscriptions.add(this.slugFormControl.valueChanges, (newSlug) => {
      if (this.originalSlug && this.originalSlug !== newSlug) {
        this.slugChanged$$.next(true);
      }
      if (newSlug && !this.slugFormControl.touched) {
        this.slugFormControl.markAsTouched();
      }
      this.cd.markForCheck();
    });
    this.subscriptions.add(this.slugFormControl.statusChanges, () => this.cd.markForCheck());
    this.linkPrefix$ = this.meetingSchedulerService.loadBookingDomain().pipe(map((domain) => domain + '/'));
    this.calendar$ = this.calendarId$$.pipe(
      filter<string>(Boolean),
      distinctUntilChanged(),
      switchMap((calendarId) => this.meetingSchedulerService.loadCalendar(calendarId)),
      shareReplay(1),
    );
    this.subscriptions.add(this.calendar$.pipe(first()), (calendar) => {
      if (calendar.slug && calendar.slug !== calendar.id && !this.slugFormControl.value) {
        this.originalSlug = calendar.slug;
        this.slugFormControl.setValue(calendar.slug);
      }
    });
    this.recommendedSlugs$ = this.calendar$.pipe(
      distinctUntilChanged((x: Calendar, y: Calendar) => x.calendarType === y.calendarType),
      switchMap((calendar) => {
        if (calendar.calendarType === CalendarType.CALENDAR_TYPE_PERSONAL) {
          return this.recommendedPersonalCalendarSlugs$;
        } else if (calendar.calendarType === CalendarType.CALENDAR_TYPE_GROUP) {
          return this.recommendedGroupCalendarSlugs$.pipe(
            map((slugsMap: { [externalId: string]: string[] }) => {
              return slugsMap[calendar.externalId] || [];
            }),
          );
        }
        return of([]);
      }),
    );
    this.loading$ = this.calendar$.pipe(
      filter(Boolean),
      map(() => false),
      startWith(true),
    );
    this.description$ = this.calendar$.pipe(
      map((calendar) => {
        const translationKey =
          calendar.calendarType === CalendarType.CALENDAR_TYPE_PERSONAL
            ? 'MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.DESCRIPTION_PERSONAL_V2'
            : 'MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.DESCRIPTION_GROUP_V2';
        return this.alternateTranslations[translationKey] || this.translate.instant(translationKey);
      }),
    );
    if (this.setInitial) {
      this.setInitialSlugIfThereIsNoSlugSet();
    }
  }

  async setSlug(slug: string): Promise<void> {
    if (!this.slugFormControl.value) {
      return;
    }
    if (
      this.slugFormControl.invalid ||
      this.originalSlug === this.slugFormControl.value ||
      !this.slugFormControl.value
    ) {
      this.saveButtonClicked.next();
      return;
    }
    this.isSaving$$.next(true);
    if (this.originalSlug) {
      const confirm = await this.matDialog
        .open<CalendarSlugConfirmationDialogComponent, CalendarSlugConfirmationDialogData>(
          CalendarSlugConfirmationDialogComponent,
          {
            maxWidth: '800px',
            data: { originalSlug: this.originalSlug, newSlug: this.slugFormControl.value },
          },
        )
        .afterClosed()
        .pipe(first())
        .toPromise();
      if (!confirm) {
        this.isSaving$$.next(false);
        return;
      }
    }

    const calendarSlugSub = from(
      this.meetingSchedulerService.setCalendarSlug(this.calendarId$$.getValue(), slug),
    ).subscribe({
      next: () => {
        this.originalSlug = slug;
        this.slugChanged$$.next(false);
        this.alertService.openSuccessSnack(
          'MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.UPDATED_SUCCESSFULLY',
        );
        this.slugFormControl.markAsPristine(); // Mark the form as pristine so we don't get unnecessary unsaved changes warnings when navigating away
        if (this.enableTracking) {
          this.analyticsService.trackEvent(
            POSTHOG_KEYS.HOST_PREFERENCE_UPDATED,
            POSTHOG_CATEGORIES.USER,
            POSTHOG_ACTIONS.CLICK,
          );
        }
        if (this.navigateAfterSave) {
          this.router.navigate(this.navigateAfterSave);
        }
      },
      error: (err) => {
        if (err && err.status === 409) {
          this.alertService.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.ALREADY_TAKEN', {
            duration: 1000000,
          });
        } else {
          this.alertService.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.UPDATE_ERROR');
        }
      },
    });

    calendarSlugSub.add(() => {
      this.saveButtonClicked.next();
      this.cd.markForCheck();
      this.isSaving$$.next(false);
    });

    this.subscriptions.addSub(calendarSlugSub);
  }

  revertSlug(): void {
    this.slugFormControl.setValue(this.originalSlug);
  }

  private slugNotTakenValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return NEVER;
      }
      if (this.originalSlug === control.value) {
        return NEVER;
      }
      // Use timer to debounce so we don't call our API for every keystroke.
      return timer(250).pipe(
        switchMap(() => {
          return this.meetingSchedulerService.isCalendarSlugTaken(control.value);
        }),
        map((taken) => {
          return taken
            ? {
                taken: this.translate.instant(
                  'MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.ALREADY_TAKEN',
                ),
              }
            : null;
        }),
        tap(() => this.cd.markForCheck()),
      );
    };
  }

  private alphaNumericAndLengthSlugValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return;
      }
      // `-` purposefully left out to avoid someone using someone else's hostId
      if (Validators.pattern(/^[a-z0-9_-]*$/g)(control)) {
        return {
          pattern: this.translate.instant(
            'MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.VALIDATION.PATTERN',
          ),
        };
      }
      const lengthValidator = Validators.compose([Validators.maxLength(50), Validators.minLength(3)]);
      if (lengthValidator(control)) {
        return {
          pattern: this.translate.instant(
            'MEETING_SCHEDULER.SCHEDULE_MEETING_SETTINGS.CALENDAR_SLUG.VALIDATION.LENGTH',
          ),
        };
      }
    };
  }

  private setInitialSlugIfThereIsNoSlugSet(): void {
    const slugHasValue$ = this.slugFormControl.valueChanges.pipe(
      startWith(this.originalSlug),
      map((slug) => Boolean(slug)),
      filter(Boolean),
      first(),
    );

    this.subscriptions.add(
      this.loading$.pipe(
        filter(Boolean),
        switchMap(() => this.recommendedSlugs$),
        switchMap((slugs) => from(slugs)),
        takeUntil(slugHasValue$),
        concatMap((slug) => this.meetingSchedulerService.isCalendarSlugTaken(slug).pipe(map((taken) => [taken, slug]))),
        filter(([taken]) => !taken),
        map(([, slug]) => slug),
        first(),
      ),
      (slug) => {
        if (!this.slugFormControl.value) {
          this.slugFormControl.setValue(slug);
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }
}
